/**
 * Vault recovery by CUSTOS support (Phase 9c, D68–D71).
 *
 * The vault secret is encrypted on the device to the CUSTOS **recovery public key**. The matching
 * private key is the owner's OFFLINE recovery key (its own 24 recovery words, kept encrypted on the
 * owner's PC and on paper — never on the server, D69). The database, API, admin panel and server
 * only ever see ciphertext. To restore, the owner's PC command decrypts the backup and immediately
 * re-encrypts ("reseals") it for a one-time key generated on the user's new device.
 *
 * Construction (standard "ECIES" pattern from audited primitives, no custom crypto):
 *   ephemeral X25519 key → shared secret with the recipient → HKDF-SHA256 → AES-256-GCM,
 *   with "additional data" binding every sealed box to its network and user.
 */
import { gcm } from "@noble/ciphers/aes.js";
import { randomBytes } from "@noble/ciphers/utils.js";
import { x25519 } from "@noble/curves/ed25519.js";
import { hkdf } from "@noble/hashes/hkdf.js";
import { sha256 } from "@noble/hashes/sha2.js";
import { bytesToHex, hexToBytes } from "@noble/hashes/utils.js";
import { base64 } from "@scure/base";
import { mnemonicToSeed } from "@custos/chain-adapters/keys";
const encoder = new TextEncoder();
const INFO = encoder.encode("custos-vault-recovery-v1");
function boxKey(shared, epk, recipient) {
    return hkdf(sha256, shared, Uint8Array.from([...epk, ...recipient]), INFO, 32);
}
/** Encrypts `plaintext` so only the holder of `recipientPublicKey` (32 bytes) can read it. */
export function sealTo(recipientPublicKey, plaintext, aad) {
    if (recipientPublicKey.length !== 32)
        throw new Error("Recipient key must be 32 bytes");
    const eph = x25519.utils.randomSecretKey();
    const epk = x25519.getPublicKey(eph);
    const key = boxKey(x25519.getSharedSecret(eph, recipientPublicKey), epk, recipientPublicKey);
    const nonce = randomBytes(12);
    try {
        const data = gcm(key, nonce, encoder.encode(aad)).encrypt(plaintext);
        return {
            v: 1,
            alg: "x25519-hkdf-sha256-aes256gcm",
            epk: base64.encode(epk),
            nonce: base64.encode(nonce),
            data: base64.encode(data),
        };
    }
    finally {
        eph.fill(0);
        key.fill(0);
    }
}
export function openSealed(secretKey, box, aad) {
    if (box.v !== 1 || box.alg !== "x25519-hkdf-sha256-aes256gcm")
        throw new Error("Unknown sealed format");
    const epk = base64.decode(box.epk);
    const me = x25519.getPublicKey(secretKey);
    const key = boxKey(x25519.getSharedSecret(secretKey, epk), epk, me);
    try {
        return gcm(key, base64.decode(box.nonce), encoder.encode(aad)).decrypt(base64.decode(box.data));
    }
    catch {
        throw new Error("Sealed box can't be opened (wrong key or data)");
    }
    finally {
        key.fill(0);
    }
}
// ───────────────────────── who / what a box is for ─────────────────────────
/** Backup held by CUSTOS: bound to the network and the user's account id. */
export const backupAad = (network, userId) => `custos-vault-recovery:v1:${network}:${userId}`;
/** Restore box for one recovery request, readable only by the new device's one-time key. */
export const restoreAad = (network, userId, requestId) => `custos-vault-restore:v1:${network}:${userId}:${requestId}`;
/**
 * The owner's recovery key pair from its OWN 24 recovery words (not the signer seed — D69).
 * FROZEN once backups exist: changing this derivation would make every backup unreadable.
 */
export function recoveryKeyFromWords(words, network) {
    const seed = mnemonicToSeed(words);
    try {
        const secretKey = hkdf(sha256, seed, encoder.encode(`custos-vault-recovery-key:${network}`), INFO, 32);
        return { secretKey, publicKey: x25519.getPublicKey(secretKey) };
    }
    finally {
        seed.fill(0);
    }
}
/** One-time key pair for a device that is waiting to receive its restored vault. */
export function newDeviceKey() {
    const secretKey = x25519.utils.randomSecretKey();
    return { secretKey, publicKey: x25519.getPublicKey(secretKey) };
}
// ───────────────────────── device side ─────────────────────────
/** Encrypts the vault secret for the CUSTOS recovery key (done on the device). */
export function sealVaultBackup(secret, recoveryPublicKeyHex, network, userId) {
    const bytes = encoder.encode(JSON.stringify(secret));
    try {
        return sealTo(hexToBytes(recoveryPublicKeyHex), bytes, backupAad(network, userId));
    }
    finally {
        bytes.fill(0);
    }
}
/** Opens the restore box with the device's one-time key → the vault secret. */
export function openRestoredVault(box, deviceSecretKey, network, userId, requestId) {
    const bytes = openSealed(deviceSecretKey, box, restoreAad(network, userId, requestId));
    try {
        const secret = JSON.parse(new TextDecoder().decode(bytes));
        if (secret.kind !== "mnemonic" && secret.kind !== "privateKeys")
            throw new Error("Unknown vault contents");
        return secret;
    }
    finally {
        bytes.fill(0);
    }
}
// ───────────────────────── owner's PC side ─────────────────────────
/**
 * On the owner's PC only (pnpm admin:recover-vault): opens a user's backup and re-encrypts it for
 * their new device. The plaintext exists only for the duration of this call and is never shown.
 */
export function resealForDevice(input) {
    const secretKey = input.recoverySecretKey;
    let plain = null;
    try {
        plain = openSealed(secretKey, input.backup, backupAad(input.network, input.userId));
        return sealTo(hexToBytes(input.devicePublicKeyHex), plain, restoreAad(input.network, input.userId, input.requestId));
    }
    finally {
        plain?.fill(0);
    }
}
/** Public half of the owner's recovery key, as hex (goes into the API's VAULT_RECOVERY_PUBLIC_KEY). */
export function recoveryPublicKeyHex(words, network) {
    const { secretKey, publicKey } = recoveryKeyFromWords(words, network);
    secretKey.fill(0);
    return bytesToHex(publicKey);
}
//# sourceMappingURL=recovery.js.map